import { supabase } from '../supabaseClient';
import type { SchoolLegalDocumentRow, SchoolLegalKind } from '../types/schoolLegalDocument';
import {
  buildListHeadVersion,
  loadSchoolLegalDocumentsCache,
  saveSchoolLegalDocumentsCache,
} from '../utils/platformGlobalAdminCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';

function mapRow(row: Record<string, unknown>): SchoolLegalDocumentRow {
  return {
    id: String(row.id),
    workspace_id: row.workspace_id == null ? null : String(row.workspace_id),
    kind: (row.kind === 'privacy' ? 'privacy' : 'terms') as SchoolLegalKind,
    body_html: String(row.body_html ?? ''),
    is_current: Boolean(row.is_current),
    created_by: row.created_by != null ? String(row.created_by) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

/** id/updated_at 외에 is_current도 포함 — 다른 버전을 현재값으로 지정할 때 updated_at을 건드리지 않는 demote 갱신이 있어서다. */
function buildLegalHeadVersion(rows: Array<{ id: string; updated_at: string; is_current: boolean }>): string {
  return buildListHeadVersion(rows.map((r) => ({ id: r.id, updated_at: `${r.updated_at}|${r.is_current ? 1 : 0}` })));
}

/**
 * 플랫폼 전역 약관·개인정보 (`workspace_id IS NULL`). LS 캐시가 있으면 `id/updated_at/is_current` head만 먼저 비교하고,
 * 동일하면 본문(body_html) 재조회 없이 캐시를 재사용한다. `forceRefresh`는 head 비교도 건너뛰고 항상 풀 조회.
 */
export async function listSchoolLegalDocuments(
  kind: SchoolLegalKind,
  options?: { forceRefresh?: boolean }
): Promise<SchoolLegalDocumentRow[]> {
  const cached = options?.forceRefresh ? null : loadSchoolLegalDocumentsCache(kind);
  if (cached?.found && cached.version) {
    const { data: headRows, error: headError } = await supabase
      .from('school_legal_documents')
      .select('id, updated_at, is_current')
      .is('workspace_id', null)
      .eq('kind', kind);
    if (!headError) {
      const headVersion = buildLegalHeadVersion(
        (headRows ?? []).map((r) => {
          const row = r as Record<string, unknown>;
          return {
            id: String(row.id),
            updated_at: String(row.updated_at ?? ''),
            is_current: Boolean(row.is_current),
          };
        })
      );
      if (headVersion === cached.version) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('플랫폼약관·목록·LS(head일치)');
        }
        return cached.rows;
      }
    }
  }
  const { data, error } = await supabase
    .from('school_legal_documents')
    .select('id, workspace_id, kind, body_html, is_current, created_by, created_at, updated_at')
    .is('workspace_id', null)
    .eq('kind', kind)
    .order('is_current', { ascending: false })
    .order('updated_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
  saveSchoolLegalDocumentsCache(kind, rows, { force: true, version: buildLegalHeadVersion(rows) });
  return rows;
}

export async function createSchoolLegalDocumentAsCurrent(
  kind: SchoolLegalKind,
  body_html: string
): Promise<SchoolLegalDocumentRow> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData.user?.id ?? null;

  const { error: demoteErr } = await supabase
    .from('school_legal_documents')
    .update({ is_current: false })
    .is('workspace_id', null)
    .eq('kind', kind);
  if (demoteErr) throw new Error(demoteErr.message);

  const { data, error } = await supabase
    .from('school_legal_documents')
    .insert({
      workspace_id: null,
      kind,
      body_html,
      is_current: true,
      created_by: uid,
    })
    .select('id, workspace_id, kind, body_html, is_current, created_by, created_at, updated_at')
    .single();
  if (error) throw new Error(error.message);
  const created = mapRow(data as Record<string, unknown>);
  const cached = loadSchoolLegalDocumentsCache(kind);
  const prev = cached.found ? cached.rows : [];
  const next = [
    created,
    ...prev.filter((row) => row.id !== created.id).map((row) => ({ ...row, is_current: false })),
  ];
  saveSchoolLegalDocumentsCache(kind, next, { force: true, version: buildLegalHeadVersion(next) });
  return created;
}

export async function updateSchoolLegalDocument(
  id: string,
  body_html: string
): Promise<SchoolLegalDocumentRow> {
  const { data, error } = await supabase
    .from('school_legal_documents')
    .update({
      body_html,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select('id, workspace_id, kind, body_html, is_current, created_by, created_at, updated_at')
    .single();
  if (error) throw new Error(error.message);
  const updated = mapRow(data as Record<string, unknown>);
  for (const kind of ['terms', 'privacy'] as const) {
    const cached = loadSchoolLegalDocumentsCache(kind);
    if (!cached.found || !cached.rows.some((row) => row.id === updated.id)) continue;
    const nextRows = cached.rows.map((row) => (row.id === updated.id ? updated : row));
    saveSchoolLegalDocumentsCache(kind, nextRows, { force: true, version: buildLegalHeadVersion(nextRows) });
  }
  return updated;
}

export async function deleteSchoolLegalDocument(id: string): Promise<void> {
  const { error } = await supabase.from('school_legal_documents').delete().eq('id', id);
  if (error) throw new Error(error.message);
  for (const kind of ['terms', 'privacy'] as const) {
    const cached = loadSchoolLegalDocumentsCache(kind);
    if (!cached.found) continue;
    const next = cached.rows.filter((row) => row.id !== id);
    if (next.length !== cached.rows.length) {
      saveSchoolLegalDocumentsCache(kind, next, { force: true, version: buildLegalHeadVersion(next) });
    }
  }
}
